import React, { useState, useEffect, useRef } from 'react';
import { Header, Loader } from 'semantic-ui-react';
import { getDashboardChart } from '~/api/admin';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  BarController,
  Title,
  Tooltip,
  Legend,
} from 'chart.js';
import { DEPLOYMENT_TYPE_COLORS } from './chartColors';

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  BarController,
  Title,
  Tooltip,
  Legend
);

interface ChartData {
  labels: string[];
  datasets: { label: string; data: number[] }[];
}

interface InvestorInviteToBidRatioChartProps {
  interval?: string;
  onIntervalChange?: (interval: string) => void;
  states?: string[];
}

const InvestorInviteToBidRatioChart: React.FC<InvestorInviteToBidRatioChartProps> = ({
  interval: intervalProp = 'all',
  states = [],
}) => {
  const [data, setData] = useState<ChartData | null>(null);
  const [loading, setLoading] = useState(true);
  const interval = intervalProp;
  const chartRef = useRef<HTMLCanvasElement>(null);
  const chartInstance = useRef<ChartJS | null>(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const params: Record<string, string> = { interval };
        if (states.length) params.states = states.join(',');
        const result = await getDashboardChart('investor-invite-to-bid-ratio', params);
        setData(result || null);
      } catch (error) {
        console.error('Error fetching investor invite to bid ratio chart data:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [interval, states]);

  useEffect(() => {
    if (!loading && data?.labels?.length && data?.datasets?.length && chartRef.current) {
      if (chartInstance.current) {
        chartInstance.current.destroy();
      }

      const ctx = chartRef.current.getContext('2d');
      if (ctx) {
        chartInstance.current = new ChartJS(ctx, {
          type: 'bar',
          data: {
            labels: data.labels,
            datasets: data.datasets.map((ds) => ({
              label: ds.label,
              data: ds.data,
              backgroundColor: DEPLOYMENT_TYPE_COLORS[ds.label] || '#999',
              borderColor: DEPLOYMENT_TYPE_COLORS[ds.label] || '#999',
              borderWidth: 1,
            })),
          },
          options: {
            responsive: true,
            maintainAspectRatio: false,
            plugins: {
              legend: {
                position: 'top',
              },
              title: {
                display: false,
              },
              tooltip: {
                callbacks: {
                  label: (context) =>
                    `${context.dataset.label}: ${context.parsed.y} invite${context.parsed.y !== 1 ? 's' : ''}`,
                },
              },
            },
            scales: {
              x: {
                title: {
                  display: true,
                  text: '',
                },
              },
              y: {
                title: {
                  display: true,
                  text: 'Count',
                },
                beginAtZero: true,
                ticks: {
                  stepSize: 1,
                },
              },
            },
          },
        });
      }

      return () => {
        if (chartInstance.current) {
          chartInstance.current.destroy();
        }
      };
    }
  }, [data, loading]);

  if (loading) {
    return (
      <div className="card">
        <Header as="h2">Invite vs Bid Submitted</Header>
        <Loader active inline="centered" />
      </div>
    );
  }

  const hasData = data?.datasets?.some((ds) => ds.data.some((v) => v > 0));

  const conversionRows =
    data?.datasets.map((ds) => {
      const invitedNoBid = ds.data[0] ?? 0;
      const submittedBid = ds.data[1] ?? 0;
      const totalInvited = invitedNoBid + submittedBid;
      const rate =
        totalInvited > 0 ? (submittedBid / totalInvited) * 100 : null;
      return {
        segment: ds.label,
        invitedNoBid,
        submittedBid,
        totalInvited,
        rate,
      };
    }) ?? [];

  return (
    <div className="card">
      <Header as="h2">Invite vs Bid Submitted</Header>
      {hasData ? (
        <>
          <div style={{ height: '320px', width: '100%' }}>
            <canvas ref={chartRef} />
          </div>
          {conversionRows.length > 0 && (
            <div style={{ marginTop: '1rem', overflowX: 'auto' }}>
              <table className="ui very basic compact table">
                <thead>
                  <tr>
                    <th>Market segment</th>
                    <th style={{ textAlign: 'right' }}>Conversion rate</th>
                  </tr>
                </thead>
                <tbody>
                  {conversionRows.map((row) => (
                    <tr key={row.segment}>
                      <td>{row.segment}</td>
                      <td style={{ textAlign: 'right' }}>
                        {row.rate != null ? `${row.rate.toFixed(1)}%` : '–'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      ) : (
        <p>No data available</p>
      )}
    </div>
  );
};

export default InvestorInviteToBidRatioChart;
